/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

/**
 * The cookie names the consent banner declares. Kept free of Angular imports so the cookie audit under
 * `e2e/audits` can compare what a live site actually sets against exactly this list.
 *
 * A trailing `*` is a prefix wildcard, as understood by the consent plugin.
 */

export const COOKIE_CONSENT_DECISION_COOKIE = 'CookieControl';

export const NECESSARY_COOKIES = [COOKIE_CONSENT_DECISION_COOKIE, 'FolioAccessToken', 'redirectAfterLogin', 'authorised_client'];

/**
 * Set by auth.potential.ly during single sign-on: host-only, HttpOnly and session-scoped, so the banner can
 * neither read nor delete them. Strictly necessary, disclosed in the published cookie statement rather than
 * in the banner's necessary list; kept here so the audit knows they are declared somewhere.
 */
export const SSO_COOKIES = ['AUTH_SESSION_ID', 'KEYCLOAK_IDENTITY', 'KEYCLOAK_SESSION', 'KEYCLOAK_LOCALE', 'KC_RESTART'];

export const ANALYTICS_COOKIES = ['_ga', '_ga_*', '_gid', '_gat_*'];

export const EMBEDDED_CONTENT_CATEGORY = 'embeddedContent';

/** Order of the optional categories as passed to the consent plugin; the plugin addresses them by index. */
export const OPTIONAL_CATEGORY_INDEX = { analytics: 0, [EMBEDDED_CONTENT_CATEGORY]: 1 } as const;

/**
 * Third parties whose cookies live on their own domain, so the banner cannot list or delete them by name.
 * The disclosure names them; the audit checks that their requests only happen when the table says so.
 */
export const THIRD_PARTY_HOSTS = {
  /** Loaded on the sign-in and sign-up screens only. Strictly necessary, anti-abuse. */
  recaptcha: ['www.google.com/recaptcha', 'www.gstatic.com/recaptcha', 'recaptcha.net'],
  /**
   * Only after the visitor allows embedded content. An author can link to any site, so this is the set seen
   * most often rather than a complete one; the disclosure covers whatever site an embed comes from.
   */
  embeddedContent: [
    'iframe.ly',
    'iframely.net',
    'youtube.com',
    'youtube-nocookie.com',
    'ytimg.com',
    'vimeo.com',
    'maps.google.com',
    'google.com/maps',
  ],
  /** Never loaded by the app itself; the marketing site writes these to the shared parent domain. */
  analytics: ['google-analytics.com', 'googletagmanager.com', 'analytics.google.com'],
  /** Removed. Any request here is a regression. */
  retired: ['driftt.com', 'drift.com', 'confirmic.com'],
} as const;

export function matchesCookieName(pattern: string, name: string): boolean {
  return pattern.endsWith('*') ? name.startsWith(pattern.slice(0, -1)) : name === pattern;
}

/**
 * The stored decision for the embedded-content category, read straight from the consent cookie: true when
 * switched on, false when switched off, null when there is no decision yet. The plugin's own API is not
 * usable until its asynchronous licence check completes, which is too late for content already rendering.
 */
export function embeddedContentDecisionFromCookie(documentCookie: string): boolean | null {
  const entry = documentCookie.split('; ').find((cookie) => cookie.startsWith(`${COOKIE_CONSENT_DECISION_COOKIE}=`));
  if (!entry) {
    return null;
  }
  try {
    const stored = JSON.parse(decodeURIComponent(entry.slice(COOKIE_CONSENT_DECISION_COOKIE.length + 1)));
    const state = stored?.optionalCookies?.[EMBEDDED_CONTENT_CATEGORY];
    return state === 'accepted' ? true : state === 'revoked' ? false : null;
  } catch {
    return null;
  }
}
